import { useEffect, useRef, useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import { PROVIDER_API, USE_MOCK_AUTH } from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "../services/apiClient";
import { showInfo } from "../utils/toast";

const POLL_MS = 12000;

let snapshot = { revision: 0, events: [] };
const listeners = new Set();

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return snapshot;
}

function publish(events) {
  snapshot = { revision: snapshot.revision + 1, events: events || [] };
  listeners.forEach((listener) => listener());
}

/** Stable id list of new events whose type matches `pattern`. Empty until one arrives. */
export function useLiveEventTick(pattern) {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return current.events
    .filter((event) => pattern.test(event.event_type || ""))
    .map((event) => event.id)
    .join(",");
}

/**
 * Polls the provider portal for portfolio uploads and case changes.
 * Mount once from the dashboard layout.
 */
export function usePortalPulse() {
  const token = useSelector((state) => state.auth.token);
  const cursorRef = useRef(null);

  useEffect(() => {
    if (!token || USE_MOCK_AUTH) return undefined;
    let stopped = false;
    cursorRef.current = null;

    async function poll() {
      if (stopped || document.hidden) return;
      try {
        const since = cursorRef.current ?? 0;
        const json = await apiRequest(`${PROVIDER_API.live}?since=${since}`, { method: "GET", token });
        const data = unwrapApiEnvelope(json);
        if (stopped || !data) return;

        const cursor = Number(data.cursor || 0);
        const isFirst = cursorRef.current == null;
        cursorRef.current = cursor;

        if (!isFirst && Array.isArray(data.events) && data.events.length) {
          publish(data.events);
          const notable = data.events.find((event) =>
            (event.event_type || "") === "portfolio.uploaded"
          );
          if (notable?.title) showInfo(notable.title);
        }
      } catch {
        /* The next poll retries. */
      }
    }

    poll();
    const timer = window.setInterval(poll, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) poll();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token]);
}

export default function useLiveRefresh() {
  usePortalPulse();
}
